import { sanitizeRichHtml } from "@/lib/sanitize-rich-html";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { BlogTOC } from "@/components/blog/BlogTOC";
import { ShareButtons } from "@/components/blog/ShareButtons";
import { publicGetPost, incrementPostView } from "@/lib/blog/blog.functions";
import { ChevronRight, Clock, ChevronLeft, ArrowRight, Tag, Eye } from "lucide-react";

const postOptions = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => publicGetPost({ data: { slug } }),
    staleTime: 60_000,
  });

function absUrl(path: string | null | undefined) {
  if (!path) return undefined;
  return path.startsWith("http") ? path : `https://fixhen.com${path}`;
}

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData(postOptions(params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const p = loaderData?.post;
    if (!p) return { meta: [{ title: "Post not found | FIXHEN" }, { name: "robots", content: "noindex" }] };
    const title = p.meta_title || `${p.title} | FIXHEN Blog`;
    const description = p.meta_description || p.excerpt || `${p.title} — FIXHEN Blog`;
    const url = `https://fixhen.com/blog/${p.slug}`;
    const image = absUrl(p.og_url);
    const ld = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: p.title,
      description,
      image: image ? [image] : undefined,
      datePublished: p.published_at,
      dateModified: p.published_at,
      author: { "@type": "Person", name: p.author_name ?? "FIXHEN" },
      publisher: {
        "@type": "Organization",
        name: "FIXHEN",
        logo: {
          "@type": "ImageObject",
          url: "https://fixhen.com/logo.png",
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      wordCount: p.word_count,
      keywords: (p.tags ?? []).join(", "),
    };
    const bc = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fixhen.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://fixhen.com/blog" },
        { "@type": "ListItem", position: 3, name: p.title, item: url },
      ],
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: p.title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { property: "article:published_time", content: p.published_at ?? "" },
        ...(p.author_name ? [{ property: "article:author", content: p.author_name }] : []),
        ...(p.tags ?? []).map((t: string) => ({ property: "article:tag", content: t })),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(ld) },
        { type: "application/ld+json", children: JSON.stringify(bc) },
      ],
    };
  },
  component: PostPage,
});

function PostPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(postOptions(slug));
  const post = data!.post;
  const toc = data!.toc;
  const related = data!.related;
  const prev = data!.prev;
  const next = data!.next;

  // Fire and forget view count — throttled to at most once per hour
  // per post per browser to prevent inflation from refreshes.
  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      try {
        const key = `blog_viewed_${post.id}`;
        const last = Number(sessionStorage.getItem(key) ?? "0");
        if (Date.now() - last < 60 * 60 * 1000) return;
        sessionStorage.setItem(key, String(Date.now()));
      } catch { /* private mode etc. */ }
      incrementPostView({ data: { id: post.id } }).catch(() => {});
    }, 2500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [post.id]);


  const shareUrl = typeof window !== "undefined" ? window.location.href : `https://fixhen.com/blog/${slug}`;

  return (
    <div>
      <AnnouncementBar />
      <SiteHeader />

      <article>
        {/* Cover */}
        <div className="relative w-full aspect-[16/7] md:aspect-[16/6] bg-slate-900 overflow-hidden">
          {post.cover_url ? (
            <img
              src={post.cover_url}
              alt={post.title}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: `${post.cover_focal_x * 100}% ${post.cover_focal_y * 100}%` }}
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 mx-auto max-w-4xl px-4 sm:px-6 py-6 md:py-10 text-white">
            <nav className="flex items-center gap-1.5 text-xs text-white/70 mb-3">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/blog" search={{} as any} className="hover:text-white">Blog</Link>
              {post.category ? (
                <>
                  <ChevronRight className="h-3 w-3" />
                  <Link to="/blog" search={{ category: post.category.slug } as any} className="hover:text-white">
                    {post.category.name}
                  </Link>
                </>
              ) : null}
            </nav>
            {post.category ? (
              <span
                className="inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow"
                style={{ background: post.category.color }}
              >
                {post.category.name}
              </span>
            ) : null}
            <h1 className="mt-3 text-3xl md:text-5xl font-black tracking-tight max-w-3xl">{post.title}</h1>
            {post.excerpt ? <p className="mt-2 text-white/85 md:text-lg max-w-3xl">{post.excerpt}</p> : null}
            <div className="mt-4 flex items-center gap-3 text-sm text-white/80 flex-wrap">
              <span className="font-semibold">{post.author_name ?? "FIXHEN"}</span>
              <span>·</span>
              <span>
                {post.published_at &&
                  new Date(post.published_at).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" /> {post.reading_minutes} min</span>
              <span>·</span>
              <span className="inline-flex items-center gap-0.5"><Eye className="h-3 w-3" /> {post.view_count}</span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <BlogTOC items={toc} />
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Share</div>
                <ShareButtons url={shareUrl} title={post.title} />
              </div>
            </div>
          </aside>

          {/* Article body */}
          <div className="max-w-3xl">
            <div
              className="blog-article prose prose-slate prose-lg max-w-none prose-headings:font-black prose-headings:tracking-tight prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-h3:text-xl prose-a:text-[#172554] prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-blockquote:border-l-[#172554] prose-blockquote:bg-slate-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg"
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(post.content_html) }}
            />

            {post.tags?.length ? (
              <div className="mt-10 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500 font-semibold">Tags:</span>
                {post.tags.map((t: string) => (
                  <Link
                    key={t}
                    to="/blog"
                    search={{ tag: t } as any}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700"
                  >
                    <Tag className="h-3 w-3 text-slate-400" /> {t}
                  </Link>
                ))}
              </div>
            ) : null}

            {/* Mobile share */}
            <div className="mt-10 lg:hidden">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Share</div>
              <ShareButtons url={shareUrl} title={post.title} />
            </div>

            {/* Prev / Next */}
            {(prev || next) && (
              <div className="mt-12 grid gap-3 md:grid-cols-2 border-t border-slate-200 pt-6">
                {prev ? (
                  <Link
                    to="/blog/$slug"
                    params={{ slug: prev.slug }}
                    className="rounded-xl border border-slate-200 p-4 hover:border-[#172554] hover:bg-slate-50 transition"
                  >
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold flex items-center gap-1">
                      <ChevronLeft className="h-3 w-3" /> Older post
                    </div>
                    <div className="mt-1 font-bold text-ink">{prev.title}</div>
                  </Link>
                ) : <div />}
                {next ? (
                  <Link
                    to="/blog/$slug"
                    params={{ slug: next.slug }}
                    className="rounded-xl border border-slate-200 p-4 hover:border-[#172554] hover:bg-slate-50 transition text-right"
                  >
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold flex items-center justify-end gap-1">
                      Newer post <ArrowRight className="h-3 w-3" />
                    </div>
                    <div className="mt-1 font-bold text-ink">{next.title}</div>
                  </Link>
                ) : <div />}
              </div>
            )}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 ? (
          <section className="border-t border-border bg-slate-50/50">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
              <h2 className="text-2xl font-extrabold text-ink mb-6">More from {post.category?.name ?? "the blog"}</h2>
              <div className="grid gap-6 md:grid-cols-3">
                {related.map((p: any) => <BlogPostCard key={p.slug} post={p} />)}
              </div>
            </div>
          </section>
        ) : null}
      </article>

      <SiteFooter />
    </div>
  );
}